import type { Theme } from '@mui/material';
import { Paper, Stack, Table, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import type { Currency } from '@weft/types';
import { CURRENCIES } from '@weft/types';
import { dayjs, getObjectPropertyOrDefault, percentage, raiseUnreachable, sum, values } from '@weft/utils';
import { withSuspenseBoundary } from 'apps/app/src/components/utility/SuspenseBoundary';
import { useGetRevenueByCurrency } from 'apps/app/src/hooks/requests/use-get-revenue-by-currency';
import { DataDashboardLoadingSkeleton } from '../DataDashboardLoadingSkeleton';
import { DataDashboardTableHeaderCell } from '../DataDashboardTableHeaderCell';
import { RevenueByCurrencyPieChart } from './revenue-by-currency-pie-chart';
import { RevenueByCurrencyTableRow } from './revenue-by-currency-table-row';

const RevenueByCurrencySection: React.FC = () => {
  const vyTheme = useTheme();
  const { data: { amountByCurrency } } = useGetRevenueByCurrency();
  const totalValue = sum(...values(amountByCurrency));
  const year = dayjs().year();

  return (
    <Paper
      variant='outlined' 
      sx={(theme) => ({
        boxShadow: theme.shadows[1],
        padding: 3,
      })}
    >
      <Typography variant='h6'>Revenue By Currency</Typography>
      <Typography
        variant='body2'
        color={theme => theme.palette.text.secondary}
      >
        {`YTD ${year}`}
      </Typography>
      <Stack
        direction='row'
        alignItems="center"
        justifyContent='space-between'
        marginTop={2}
        gap={4}
      >
        <div className='aspect-square h-[240px] grow'>
          <RevenueByCurrencyPieChart amountByCurrency={amountByCurrency}/>
        </div>
        <Table
          size='small'
        >
          <TableHead>
            <TableRow>
              <DataDashboardTableHeaderCell
                text='Currency'
                align='left'
              />
              <DataDashboardTableHeaderCell
                text='Percentage'
                align='left'
              />
              <DataDashboardTableHeaderCell
                text='Total sum'
                align='right'
              />
            </TableRow>
          </TableHead>
          {values(CURRENCIES).map(currency => 
            (
              <RevenueByCurrencyTableRow
                currency={currency}
                total={getObjectPropertyOrDefault(amountByCurrency, currency, 0)}
                color={getRowColorByCurrency(currency, vyTheme)}
                percentage={percentage(getObjectPropertyOrDefault(amountByCurrency, currency, 0), totalValue)}
              />
            ))}
        </Table>
      </Stack>
    </Paper>
  );
};

function getRowColorByCurrency(currency: Currency, theme: Theme) {
  switch (currency) {
    case CURRENCIES.Usd:
      return theme.palette.success.light;
    case CURRENCIES.Eur:
      return theme.palette.primary.light;
    case CURRENCIES.Gbp:
      return theme.palette.warning.light;
    case CURRENCIES.Ils:
      return theme.palette.error.light;
    default:
      return raiseUnreachable(currency);
  }
}

const RevenueByCurrencyWithSuspense = withSuspenseBoundary(RevenueByCurrencySection, { suspenseProps: { fallback: <DataDashboardLoadingSkeleton/> } });

export { RevenueByCurrencyWithSuspense as RevenueByCurrencySection };